/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { sharedStyles } from '../../styles';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { generateDecadeData } from '../../utils/decade-data-generator.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { MultiItemFocusBlurMixin } from '../../utils/multi-item-focus-blur-mixin.js';

/**
 * Component that allows to select years from a
 * set of decades.
 *
 * @prop {Number} months - The number of views to be shown. Example months=2 shows 2 views of month selector starting from the given year.
 * The first view will show given month calendar. The second view shows next month calendar.
 * Any number of views can be generated by giving value to months property.
 * @prop {Number} decade - Defines the starting decade of the year-selector view .
 * @prop {Object} todaysDate - Today's Date of type Date.
 * @prop {Object} selectedDate - The selected date for this picker.
 * @prop {Boolean} futureDateDisable - Whether future dates should be disabled or not.
 * @prop {Boolean} pastDateDisable - Whether past dates should be disabled or not.
 * @prop {Object} minDate - The minimum date below which all dates will be disabled. `minDate` value should be of type Date.
 * @prop {Object} maxDate - The maximum date above which all dates will be disabled. `maxDate` value should be of type Date.
 * @prop {Boolean} responsive - Defines if this component should show responsive styles or not.
 * @fires year-clicked - Dispatched when clicked on a year in a decade. The clicked year is passed as part of detail object of event.
 */

export class SHPrivateYearSelector extends MultiItemFocusBlurMixin(LitElement) {
  static get properties() {
    return {
      months: { type: Number },
      decade: { type: Number },
      todaysDate: { type: Object },
      selectedDate: { type: Object },
      futureDateDisable: { type: Boolean },
      pastDateDisable: { type: Boolean },
      minDate: { type: Object },
      maxDate: { type: Object },
      responsive: { type: Boolean, reflect: true },
      _yearViewDatas: { type: Array },
    };
  }
  static get styles() {
    return [
      super.styles,
      sharedStyles,
      css`
        .months {
          display: flex;
          flex-direction: row;
          grid-gap: 0.5rem;
          background-color: var(--base-4);
          width: fit-content;
          box-sizing: border-box;
          box-shadow: var(--shadow-overlay);
          overflow-x: hidden;
        }
        .date-panel {
          display: block;
          width: 18rem;
          box-sizing: border-box;
          padding: 1rem 0.5rem 1.75rem 0.5rem;
          display: flex;
          flex-direction: column;
          height: 21rem;
        }
        .header-area {
          height: 1.5rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
        .month-and-year-display {
          font: var(--title-1);
          color: var(--text-primary);
          text-align: center;
          line-height: 1.5rem;
          box-sizing: border-box;
        }
        .date-grid {
          display: grid;
          grid-template-rows: repeat(4, 3rem);
          grid-template-columns: repeat(3, 4rem);
          grid-gap: 1rem;
          margin: 1.75rem 1.5rem 0rem 1.5rem;
          box-sizing: border-box;
          width: 14rem;
          align-items: center;
        }
        .icon-dummy {
          width: 2rem;
          height: 2rem;
        }
        .year {
          font: var(--body-1);
          color: var(--text-primary);
          text-align: center;
          height: 2rem;
          line-height: 2rem;
          border-radius: 1rem;
          cursor: pointer;
        }
        :host(:not(.touch-device)) .year:hover:not(.selected) {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .year.selected:not(.other-decade):not(.disabled) {
          background-color: rgb(var(--ui-3), var(--opacity-1)) !important;
          font: var(--title-1);
          color: var(--text-white);
          line-height: 2rem;
        }
        .year.today:not(.other-decade):not(.selected):not(.disabled) {
          font: var(--title-1);
          color: var(--text-highlight);
          line-height: 2rem;
        }
        .year.other-decade:not(.disabled) {
          color: var(--text-disabled);
          pointer-events: none;
        }
        .year.disabled {
          color: var(--text-disabled);
          pointer-events: none;
        }
        .icon-dummy {
          width: 2rem;
          height: 2rem;
        }
        :host([responsive]) .months {
          justify-content: center;
          width: 100%;
          box-shadow: none;
          background-color: transparent;
        }
        :host {
          display: block;
        }
      `,
    ];
  }
  constructor() {
    super();
    this.selectedDate = null;
    this.minDate = null;
    this.maxDate = null;
    this._yearViewDatas = [];
  }
  render() {
    return html`<div class="months">
      ${this._yearViewDatas.map(
        (decadeData, decadeIndex) => html`<div class="date-panel">
          <div class="header-area">
            ${decadeIndex === 0
              ? html`<sh-icon
                  icon="arrow-left-s"
                  size="m"
                  button
                  @click="${() => {
                    this.decade -= 10;
                    this.generateYearViewData();
                  }}"
                ></sh-icon>`
              : html`<div class="icon-dummy"></div>`}
            <div class="month-and-year-display">
              ${decadeData[1].date.getFullYear()} - ${decadeData[10].date.getFullYear()}
            </div>
            ${decadeIndex === this._yearViewDatas.length - 1
              ? html`<sh-icon
                  icon="arrow-right-s"
                  size="m"
                  button
                  @click="${() => {
                    this.decade += 10;
                    this.generateYearViewData();
                  }}"
                ></sh-icon>`
              : html`<div class="icon-dummy"></div>`}
          </div>
          <div class="date-grid">
            ${decadeData.map(
              (yearData, yearIndex) =>
                html`<span
                  class="${classMap(this._getComputedClasses(yearData, yearIndex))}"
                  @click="${() => {
                    dispatchCustomEvent(this, 'year-clicked', {
                      clickedYear: yearData.date.getFullYear(),
                    });
                  }}"
                  tabindex="${yearData.isDisabled ? '-1' : '0'}"
                >
                  ${yearData.date.getFullYear()}
                </span>`
            )}
          </div>
        </div> `
      )}
    </div>`;
  }
  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    } else {
      this.setAttribute('tabindex', 0);
    }
  }
  generateYearViewData() {
    let startdecade = this.decade;
    let yearViewDatas = [];
    for (let i = 0; i < this.months; i++) {
      yearViewDatas.push(
        generateDecadeData(
          startdecade,
          this.todaysDate,
          this.selectedDate,
          this.futureDateDisable,
          this.pastDateDisable,
          this.minDate,
          this.maxDate
        )
      );
      startdecade += 10;
    }
    this._yearViewDatas = yearViewDatas;
  }
  /**
   *
   * @param {YearDataContainer} yearObject
   */
  _getComputedClasses(yearObject, yearIndex) {
    return {
      year: true,
      today: yearObject.isThisYear,
      selected: yearObject.isSelected,
      disabled: yearObject.isDisabled,
      'other-decade': yearIndex === 0 || yearIndex === 11,
      'focus-item': true,
    };
  }
  _enterKeyAction(focusedElement) {
    if (focusedElement !== this) {
      focusedElement.click();
    }
  }
}
customElements.define('sh-private-year-selector', SHPrivateYearSelector);
